import React from 'react'
import ReactTable from 'react-table-6'
import { I18n } from 'react-redux-i18n'
import { formatCurrency, formatProfitLoss, formatPrice } from '../lib/currency-formatter'
import { AppMode } from '../types'
import {
  PricesExtended,
  TradeObject,
  PriceExtended,
  TradeCommandType,
} from '@bdswiss/mt4-connector'
import Spinner from './Spinner'
import { FC, useContext } from 'react'
import { TableContext, ExpandedAction } from './contexts/TableContext'

const emptyPositions: TradeObject[] = []

export interface Props {
  accountLoaded: boolean
  row?: any
  assets: PricesExtended
  positions: TradeObject[]
  currency: string
  locale: string
  onSelectPosition: (order: number, expanded?: boolean) => void
  onSetChartMode: () => void
  onCloseTrade: (order: number, uuid?: string) => void
  onSetAppMode: (mode: AppMode) => void
  onSetSymbol: (symbol: string, tradeType: TradeCommandType) => void
}
interface ContentProps extends Props {
  expandedPositionsState: React.ReducerState<
    (state: { [p: string]: boolean }, action: ExpandedAction) => { [p: string]: boolean }
  >
  expandedPositionsDispatch: React.Dispatch<
    React.ReducerAction<
      (state: { [p: string]: boolean }, action: ExpandedAction) => { [p: string]: boolean }
    >
  >
}
export interface State {
  asset: PriceExtended
}

const AssetPositionsTable: FC<Props> = (props: Props) => {
  const { expandedPositionsState, expandedPositionsDispatch } = useContext(TableContext)
  return (
    <AssetPositionsTableContent
      {...props}
      expandedPositionsState={expandedPositionsState}
      expandedPositionsDispatch={expandedPositionsDispatch}
    />
  )
}

class AssetPositionsTableContent extends React.Component<ContentProps, State> {
  private columns = [
    {
      headerStyle: { display: 'none' },
      minWidth: 30,
      Cell: (props: any) => {
        const { type } = props.original
        return (
          <div
            className={/buy/i.test(type) ? 'bdswiss-table--positive' : 'bdswiss-table--negative'}
          >
            &nbsp;&nbsp;
            <i className={/buy/i.test(type) ? 'icon-buy-new' : 'icon-sell-new'} />
            {I18n.t(type)}
          </div>
        )
      },
    },
    {
      headerStyle: { display: 'none' },
      minWidth: 60,
      className: 'text-left',
      Cell: (props: any) => {
        const { investment, volume, symbol } = props.original
        const { locale, currency } = this.props
        const investmentValue = investment
          ? formatCurrency(investment, currency, locale)
          : `${volume} ${I18n.t('lots', { count: volume === 1 ? 1 : 0 })}`
        const asset = this.props.assets[symbol]
        const value = asset
          ? formatCurrency(volume * asset.lotSize, asset.contractType, this.props.locale)
          : investmentValue
        return (
          <div title={value}>
            <span className="inline-label">{I18n.t('value')}: </span>
            {value.replace(/0+$/g, '').replace(/\.$/, '.00')}
          </div>
        )
      },
    },
    {
      headerStyle: { display: 'none' },
      minWidth: 70,
      className: 'text-left',
      Cell: (props: any) => {
        const { openPrice, symbol } = props.original
        const asset = this.props.assets[symbol]
        const precision = asset ? asset.digits : 5
        return (
          <div title={openPrice}>
            <span className="inline-label">{I18n.t('openPrice')}: </span>
            {formatPrice(openPrice, precision, '')}
          </div>
        )
      },
    },
    {
      headerStyle: { display: 'none' },
      minWidth: 50,
      className: 'text-left',
      Cell: (props: any) => {
        const { leverage } = props.original
        return leverage ? (
          <div title={leverage}>
            <span className="inline-label">{I18n.t('leverage')}: </span>1:{leverage}
          </div>
        ) : null
      },
    },
    {
      headerStyle: { display: 'none' },
      minWidth: 50,
      className: 'text-left',
      Cell: (props: any) => {
        const { profit } = props.original
        const { currency, locale } = this.props
        const pl = formatProfitLoss(profit, currency, locale, currency === 'CUD' ? {precision: 2} : {})
        return (
          <div title={pl}>
            <span className="inline-label">{I18n.t('profit')}: </span>
            <span className={'bdswiss-table--' + (profit > 0 ? 'positive' : 'negative')}>{pl}</span>
          </div>
        )
      },
    },
    {
      headerStyle: { display: 'none' },
      minWidth: 25,
      Cell: (props: any) => {
        const { onSelectPosition, onSetAppMode } = this.props
        return (
          <div
            className="d-flex align-items-center text-primary"
            onClick={e => {
              e.stopPropagation()
              onSetAppMode('assetPosition')
              onSelectPosition(props.original.order, true)
            }}
          >
            <i className="icon-edit-new" />
            {I18n.t('edit')}
          </div>
        )
      },
    },
    {
      headerStyle: { display: 'none' },
      minWidth: 25,
      Cell: (props: any) => {
        const { order } = props.original
        return (
          <div
            className="d-flex align-items-center text-primary"
            onClick={e => {
              e.stopPropagation()
              this.props.onCloseTrade(order, order.uuid)
            }}
          >
            <i className="icon-close-new" />
            {I18n.t('close')}
          </div>
        )
      },
    },
    {
      id: 'openDate',
      accessor: 'openTime',
      headerStyle: { display: 'none' },
      width: 0,
      Cell: (props: any) => '',
    },
  ]

  constructor(props: ContentProps) {
    super(props)

    const asset = props.assets[props.row.symbol]

    this.state = {
      // showAll: false,
      asset: asset!,
    }
    this.toggleExpanded = this.toggleExpanded.bind(this)
  }

  public render() {
    if (!this.props.assets || !this.props.accountLoaded) {
      return <Spinner />
    }
    const { positions: allPositions, onSetSymbol, row } = this.props
    const positions = row ? allPositions.filter(p => p.symbol === row.symbol) : emptyPositions
    const length = positions.length
    if (!length) {
      return null
    }
    const showAll = row ? this.props.expandedPositionsState[row.symbol] : false
    return (
      <div className="InlinePositions" onClick={() => onSetSymbol(row.symbol, 'buy')}>
        <div className="bdswiss-table--regular pad-20">
          {length} {I18n.t('openPositions')}
        </div>
        <ReactTable
          noDataText={I18n.t('noItemsToDisplay')}
          data={positions}
          columns={this.columns}
          className="bdswiss-table"
          showPagination={false}
          sortable={false}
          defaultSorted={[
            {
              id: 'openDate',
              desc: true,
            },
          ]}
          minRows={1}
          pageSize={showAll ? length : 3}
        />
        {length > 3 ? (
          <div
            className="d-flex justify-content-center cursor-pointer text-primary"
            onClick={this.toggleExpanded}
          >
            <div className={'wt-expander wte-primary wte' + (showAll ? '-up' : '-down')}>
              &nbsp;
            </div>
            <button className="ButtonLink">{I18n.t(showAll ? 'showLess' : 'showMore')}</button>
          </div>
        ) : null}
      </div>
    )
  }

  private toggleExpanded() {
    const row = this.props.row
    if (row) {
      this.props.expandedPositionsDispatch({ type: 'toggle', key: row.symbol })
    }
  }
}

export default AssetPositionsTable
